import classNames from "classnames/dedupe";
import type { FlattenedSectionProps } from "..";
import {
  useAnswer,
  useVisibilityMap,
} from "../../../../features/questionnaire";
import { useEnvironmentValues } from "../../../../hooks/environment/useEnvironment";
import { useLocale } from "../../../../hooks/useLocale";
import { useShouldAnimateQuestions } from "../../../../hooks/useShouldAnimateQuestions";
import { useShouldHideSectionTitle } from "../../../../hooks/useShouldHideSectionTitle";
import { Preamble, Postamble } from "../../Amble";
import { Question } from "../Question";
import { isQuestion } from "../Question/types";
import {
  getReactInterventionComponent,
  InterventionWrapper,
} from "@simplybusiness/mobius-interventions";

const FIRST_NAME_QUESTION_NAME = "Customer first name";
const RISK_ADDRESS_SECTION_ID = "Risk address";

export const Section = ({ ref, ...props }: FlattenedSectionProps) => {
  const {
    id,
    preamble,
    postamble,
    preambleName,
    questions,
    className,
    interventions = [],
  } = props;

  const { site } = useEnvironmentValues();
  const { locale } = useLocale();
  const shouldAnimate = useShouldAnimateQuestions();
  const shouldHideTitle = useShouldHideSectionTitle(id);
  const visibilityMap = useVisibilityMap();

  const hasVisibleQuestions = questions.some(
    q => visibilityMap[q.name] ?? q.visible,
  );

  const firstName = useAnswer(FIRST_NAME_QUESTION_NAME);

  const title =
    id === RISK_ADDRESS_SECTION_ID && !!firstName
      ? `Hi ${String(firstName)}`
      : props.title;

  if (!hasVisibleQuestions) {
    return null;
  }

  const classes = classNames("mobius", "mobius-section", className);

  return (
    <section data-testid="mobius-section" ref={ref} className={classes}>
      {title && !shouldHideTitle && (
        <h2 className="mobius-section__title">{title}</h2>
      )}
      <Preamble
        amble={preamble}
        ambleName={preambleName}
        site={site}
        locale={locale}
      />
      <div className="mobius-section__question-container">
        {questions.filter(isQuestion).map(question => (
          <Question
            key={question.name}
            {...question}
            shouldAnimate={shouldAnimate}
          />
        ))}
      </div>
      <Postamble amble={postamble} site={site} locale={locale} />
      {interventions.map((intervention: string) => {
        const interventionElement = getReactInterventionComponent(intervention);

        if (interventionElement) {
          return (
            <InterventionWrapper key={intervention} name={intervention}>
              {interventionElement}
            </InterventionWrapper>
          );
        }
        return null;
      })}
    </section>
  );
};

Section.displayName = "Section";
